import { RankingTabs } from './RankingTabs';
import { rankingOrigin } from './rankingTypes';
import '@/styles/ranking-layout.css';
import '@/styles/character-profile-layout.css';
import { useEffect, useRef } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { SimpleMenu } from '@/components/MenuGrid';
import { useLocale } from '@/context/LocaleContext';
import { installWebViewAuthBridge } from '@/lib/axios';
import { useCharacterSideData } from '@/hooks/useCharacterProfile';
import { CharacterStatsPanel } from './CharacterStatsPanel';
import { initCharacterProfileLikeCI4 } from './characterProfileInit';
import { patchSwitchInventoryTabForBuffs } from './characterProfileItemTooltip';

export function CharacterProfilePage() {
  const { charId: charIdParam } = useParams<{ charId: string }>();
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const origin = rankingOrigin(searchParams.get('from'));
  const profileRef = useRef<HTMLDivElement>(null);
  const sideRef = useRef<ReturnType<typeof useCharacterSideData>['data']>(null);
  const unpatchRef = useRef<(() => void) | null>(null);

  const charId = Number(charIdParam);
  if (!charIdParam || Number.isNaN(charId) || charId <= 0) {
    return <Navigate to="/ranking" replace />;
  }

  const sideQuery = useCharacterSideData(charId);
  sideRef.current = sideQuery.data ?? null;

  useEffect(() => {
    installWebViewAuthBridge();
    window.TRANSLATIONS = {
      inventory: t('ranking_profile_inventory'),
      avatar: t('ranking_profile_avatar'),
      level: t('ranking_profile_level'),
      strength: t('ranking_profile_strength'),
      intelligence: t('ranking_profile_intelligence'),
      guild: t('ranking_profile_guild'),
      no_guild: t('ranking_profile_no_guild'),
    };
  }, [t]);

  useEffect(() => {
    const content = profileRef.current;
    if (!content) return;

    let cancelled = false;

    content.innerHTML = `
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>${t('ranking_profile_loading')}</p>
      </div>
    `;

    void (async () => {
      try {
        await initCharacterProfileLikeCI4(charId, content, sideRef.current ?? null);
        if (cancelled) return;
        unpatchRef.current?.();
        unpatchRef.current = patchSwitchInventoryTabForBuffs(() => sideRef.current ?? null);
      } catch {
        if (cancelled) return;
        content.innerHTML = `
          <div class="error-message" style="text-align:center;padding:40px;color:#ff6b6b;">
            <h3>${t('ranking_profile_error_title')}</h3>
            <p>${t('ranking_profile_error_message')}</p>
            <a href="/ranking" class="btn-back" style="display:inline-block;margin-top:20px;padding:10px 20px;background:#e6c98a;color:#1a0a05;text-decoration:none;border-radius:4px;">
              ${t('ranking_profile_back')}</a>
          </div>
        `;
      }
    })();

    return () => {
      cancelled = true;
      unpatchRef.current?.();
      unpatchRef.current = null;
    };
  }, [charId, t]);

  // Re-seed buffs cuando side data llega después del render (no bloquea perfil)
  useEffect(() => {
    if (!sideQuery.data) return;
    window.ItemTooltip?.cache.set(`buffs_${charId}`, sideQuery.data.buffs);
  }, [charId, sideQuery.data]);

  return (
    <div className="container-ingame fade-in">
      <div className="calendar-container character-details-page">
        <SimpleMenu title={t('ranking')} backTo={`/ranking/${origin}`}><RankingTabs active={origin} /></SimpleMenu>
        <div className="calendar-info-container">
          <div className="character-profile-container" style={{ display: 'flex', gap: 15 }}>
            <div
              className="character-profile-col"
              style={{ flex: '0 0 41.67%', maxWidth: '41.67%', minWidth: 0 }}
            >
              <div
                id="characterProfileContent"
                ref={profileRef}
                className="character-profile-wrapper"
              />
            </div>
            <div className="character-profile-col" style={{ flex: 1, minWidth: 0 }}>
              <CharacterStatsPanel charId={charId} side={sideQuery.data ?? null} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
